import { useState, useRef, useEffect } from "react";
import cx from "classnames";
import { Input } from "@repo/ping-design";
import type { RenderEditCellProps } from "react-data-grid";
import { useCellSave } from "../useCellSave";
import type { EditableFieldConfig } from "../editableFields";
import type { TopBuildingsListRow } from "../TopBuildingsList";

type Props = RenderEditCellProps<TopBuildingsListRow> & {
  config: EditableFieldConfig;
  onSave: (value: number | null) => Promise<boolean>;
};

export function NumericCellEditor({ row, column, config, onSave, onClose }: Props) {
  const initial = String(row.values[column.key] ?? "");
  const [value, setValue] = useState(initial);
  const { status, runSave } = useCellSave();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const valueRef = useRef(initial);
  // Prevents double-save when both onBlur and unmount cleanup fire.
  const savedRef = useRef(false);

  const clamp = (raw: string): string => {
    if (raw === "") return raw;
    const n = Number(raw);
    if (Number.isNaN(n)) return raw;
    const lo = config.min ?? -Infinity;
    const hi = config.max ?? Infinity;
    return String(Math.min(hi, Math.max(lo, n)));
  };

  const handleSave = async (): Promise<boolean> => {
    if (savedRef.current) return true;
    const clamped = clamp(valueRef.current);
    if (clamped === initial) return true;
    savedRef.current = true;
    const ok = await runSave(() => onSave(clamped === "" ? null : Number(clamped)));
    if (!ok) savedRef.current = false;
    return ok;
  };

  // Keep latest values accessible in the unmount cleanup (no stale closure).
  const unmountSaveRef = useRef(() => {});
  unmountSaveRef.current = () => {
    if (savedRef.current) return;
    const clamped = clamp(valueRef.current);
    if (clamped === initial) return;
    savedRef.current = true;
    // Fire-and-forget: can't await in cleanup. Covers Tab / click-another-cell
    // where react-data-grid closes the editor via mousedown before blur fires.
    onSave(clamped === "" ? null : Number(clamped));
  };

  useEffect(() => () => unmountSaveRef.current(), []);

  return (
    <div
      ref={wrapperRef}
      className={cx("TopBuildingsList__CellEditorWrapper", {
        "TopBuildingsList__CellEditorWrapper--Error": status === "error",
      })}
      onBlur={async (e) => {
        if (wrapperRef.current?.contains(e.relatedTarget as Node)) return;
        const ok = await handleSave();
        if (ok) onClose(true);
      }}
    >
      <Input
        name={column.key}
        value={value}
        autoFocus
        disabled={status === "saving"}
        onChange={(_name, v) => {
          const clamped = clamp(String(v));
          setValue(clamped);
          valueRef.current = clamped;
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            savedRef.current = true;
            onClose(false);
          }
          if (e.key === "Enter") {
            e.stopPropagation();
            handleSave().then((ok) => {
              if (ok) onClose(true);
            });
          }
        }}
      />
    </div>
  );
}
